import { TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";
import Checkbox from "@saleor/components/Checkbox";
import DecimalFormat from "@saleor/components/DecimalFormat";
import MyTypography from "@saleor/components/MyTypography";
import NumberFormat from "@saleor/components/NumberFormat";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import { BIG_ROW_NUMBER_SET } from "@saleor/components/TablePagination/constants";
import { TablePaginationCustom } from "@saleor/components/TablePaginationCustom";
import { makeStyles } from "@saleor/macaw-ui";
import { renderCollection } from "@saleor/misc";
import { ListProps } from "@saleor/types";
import { convertToRolExport } from "@saleor/utils/data/convertUnit";
import classNames from "classnames";
import React from "react";
import { useIntl } from "react-intl";

interface TableDetailProps extends ListProps {
  listProduct: any[];
  setListProduct: (value) => void;
  listProductChange: any[];
  setListProductChange: (value) => void;
  totalCount?: number;
  latestPageItemNumber: number | null;
  onPreviousFirstPage: () => void;
  onNextLastPage: () => void;
}

const useStyles = makeStyles(
  theme => ({
    root: {
      marginBottom: 120,
      marginTop: 60
    },
    countTitle: {
      marginBottom: 16
    },
    tableRoot: {
      marginBottom: 20,
      "& .MuiTableBody-root": {
        "& .MuiTableCell-root": {
          paddingRight: 10,
          borderTop: "solid 1px #FFF"
        }
      },
      "& .MuiTableCell-root": {
        paddingRight: 10
      },
      border: `1px solid ${theme.palette.grey[300]}`,
      overflow: "hidden",
      borderRadius: 6
    },
    normalText: {
      fontWeight: 700,
      color: theme.palette.grey[600]
    },
    cellBackGround: {
      background: `${theme.palette.grey[200]} !important`
    },
    cellCheckbox: {
      paddingLeft: "10px !important",
      paddingRight: "10px !important",
      width: 64
    },
    cellName: {
      width: "35%"
    },
    errorText: {
      color: theme.palette.error.light,
      marginTop: 10
    },
    lastCol: {
      paddingRight: "24px !important"
    }
  }),
  { name: "TableDetail" }
);

const totalCols = 8;

const TableDetail: React.FC<TableDetailProps> = props => {
  const {
    pageInfo,
    onNextPage,
    onPreviousPage,
    settings,
    listProduct,
    setListProduct,
    listProductChange,
    setListProductChange,
    totalCount,
    onUpdateListSettings,
    disabled,
    onPreviousFirstPage,
    onNextLastPage,
    latestPageItemNumber
  } = props;
  const classes = useStyles();
  const intl = useIntl();

  const handleChangeQuantity = (event, lineId) => {
    const value = event.target.value;
    const list = [...listProduct];
    const lineChange = list.find(item => item.id === lineId);
    lineChange.quantity = value;
    let limit =
      lineChange.remainingQuantityEx / lineChange.weight -
      lineChange.cartQuantity;
    if (lineChange.salesUnit === "EA") {
      limit = lineChange.remainingQuantityEx - lineChange.cartQuantity;
    }
    lineChange.errorQuantity = value > limit || (!value && lineChange.selected);
    if (!!lineChange.quantity) {
      lineChange.selected = true;
    } else {
      // lineChange.selected = false;
      // lineChange.errorQuantity = false;
    }
    setListProduct(list);
  };

  const getSelectedLine = () => listProduct.filter(item => item.selected);

  const getCheckedHeader = () => listProduct.every(item => item.selected);

  const getIndeterminateHeader = () => {
    const listSelectedLength = getSelectedLine().length;
    return listSelectedLength > 0 && listSelectedLength < listProduct.length;
  };

  const onChangeCheckItem = (event, lineId) => {
    const checked = event.target.checked;
    const list = [...listProduct];
    const lineChange = list.find(item => item.id === lineId);
    lineChange.selected = checked;
    let limit =
      lineChange.remainingQuantityEx / lineChange.weight -
      lineChange.cartQuantity;
    if (lineChange.salesUnit === "EA") {
      limit = lineChange.remainingQuantityEx - lineChange.cartQuantity;
    }
    if (!lineChange.quantity) {
      lineChange.errorQuantity = checked;
    } else {
      if (lineChange.quantity > limit) {
        lineChange.errorQuantity = lineChange.selected;
      }
    }
    //
    const linesTemp = [...listProductChange];
    const lineTempChange = linesTemp.find(item => item.id === lineId);
    if (lineTempChange) {
      lineTempChange.selected = checked;
    }
    setListProduct(list);
    setListProductChange(linesTemp);
  };

  const onClickCheckHeader = () => {
    const list = [...listProduct];
    const linesTemp = [...listProductChange];
    const listSelected = getSelectedLine();
    if (listSelected.length < listProduct.length) {
      list.forEach(item => {
        item.selected = true;
        let limit = item.remainingQuantityEx / item.weight - item.cartQuantity;
        if (item.salesUnit === "EA") {
          limit = item.remainingQuantityEx - item.cartQuantity;
        }
        if (item.quantity) {
          item.errorQuantity = item.quantity > limit;
        } else {
          item.errorQuantity = true;
        }
      });
    } else if (listSelected.length === listProduct.length) {
      list.forEach(item => {
        item.selected = false;
        item.errorQuantity = false;
        const lineTemp = linesTemp.find(temp => temp.id === item.id);
        if (lineTemp) {
          lineTemp.selected = false;
        }
      });
    }
    setListProduct(list);
    setListProductChange(linesTemp);
  };

  const getErrorText = line => {
    if (line.errorQuantity && line.selected) {
      return line.quantity
        ? intl.formatMessage({
            defaultMessage: "จำนวนที่สั่งมีมากกว่าจำนวนคงเหลือ"
          })
        : intl.formatMessage({ defaultMessage: "โปรดระบุจำนวนที่ต้องการ" });
    }
  };

  return (
    <div className={classes.root}>
      <MyTypography variant={"h7"} className={classes.countTitle}>
        รายการสินค้า ({totalCount})
      </MyTypography>
      <div className={classes.tableRoot}>
        <ResponsiveTable>
          <TableHead>
            <TableRow>
              <TableCellHeader className={classes.cellCheckbox}>
                <Checkbox
                  size={"small"}
                  checked={getCheckedHeader()}
                  indeterminate={getIndeterminateHeader()}
                  onClick={onClickCheckHeader}
                  id="checkbox-header"
                  name="checkbox-header"
                />
              </TableCellHeader>
              <TableCellHeader className={classes.cellName}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.normalText}
                >
                  {intl.formatMessage({ defaultMessage: "รายละเอียดสินค้า" })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader width={220}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.normalText}
                >
                  {intl.formatMessage({ defaultMessage: "จำนวนสั่ง" })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.normalText}
                >
                  {intl.formatMessage({ defaultMessage: "หน่วย" })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader
                textAlign="right"
                className={classes.cellBackGround}
              >
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.normalText}
                >
                  {intl.formatMessage({ defaultMessage: "จำนวน" })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader
                textAlign="right"
                className={classes.cellBackGround}
              >
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.normalText}
                >
                  {intl.formatMessage({ defaultMessage: "คงเหลือ" })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader
                textAlign={"center"}
                className={classes.cellBackGround}
              >
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.normalText}
                >
                  {intl.formatMessage({ defaultMessage: "หน่วย" })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader
                textAlign="right"
                className={classNames(classes.cellBackGround, classes.lastCol)}
              >
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.normalText}
                >
                  {intl.formatMessage({ defaultMessage: "ราคา/หน่วย" })}
                </MyTypography>
              </TableCellHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {listProduct.length ? (
              renderCollection(listProduct, line => (
                <TableRow key={line.id}>
                  <TableCell className={classes.cellCheckbox}>
                    <Checkbox
                      size={"small"}
                      onChange={e => onChangeCheckItem(e, line.id)}
                      checked={line.selected}
                      id={`checkbox-${line.id}`}
                    />
                  </TableCell>
                  <TableCell className={classes.cellName}>
                    <MyTypography variant={"body3"}>
                      {line.product.name}
                    </MyTypography>
                  </TableCell>
                  <TableCell width={220}>
                    <NumberFormat
                      onChange={event => handleChangeQuantity(event, line.id)}
                      allowLeadingZeros
                      decimalScale={0}
                      placeholder={"ระบุจำนวน"}
                      value={line.quantity}
                      error={line.errorQuantity}
                      id={`quantity-${line.id}`}
                    />
                    <MyTypography
                      variant={"caption"}
                      className={classes.errorText}
                    >
                      {getErrorText(line)}
                    </MyTypography>
                  </TableCell>
                  <TableCell>
                    <MyTypography variant={"body3"}>
                      {convertToRolExport(line.weightUnit)}
                    </MyTypography>
                  </TableCell>
                  <TableCell align="right" className={classes.cellBackGround}>
                    <DecimalFormat
                      value={line.totalQuantity}
                      isGrayDecimal
                      countDecimal={3}
                    />
                  </TableCell>
                  <TableCell align="right" className={classes.cellBackGround}>
                    <DecimalFormat
                      value={line.remainingQuantity}
                      isGrayDecimal
                      countDecimal={3}
                    />
                  </TableCell>
                  <TableCell
                    align={"center"}
                    className={classes.cellBackGround}
                  >
                    <MyTypography variant={"body3"}>
                      {line.weightUnit}
                    </MyTypography>
                  </TableCell>
                  <TableCell
                    align="right"
                    className={classNames(
                      classes.cellBackGround,
                      classes.lastCol
                    )}
                  >
                    {line.pricePerUnit ? (
                      <DecimalFormat
                        value={line.pricePerUnit}
                        isGrayDecimal={false}
                        countDecimal={2}
                      />
                    ) : (
                      ""
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={totalCols}>
                  <Skeleton />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </ResponsiveTable>
      </div>
      <TablePaginationCustom
        hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
        hasPreviousPage={
          pageInfo && !disabled ? pageInfo.hasPreviousPage : false
        }
        onNextPage={onNextPage}
        onPreviousPage={onPreviousPage}
        totalCount={totalCount}
        selectColor="#007E92"
        settings={settings}
        onUpdateListSettings={onUpdateListSettings}
        isBackFistPage
        isNextLastPage
        latestPageItemNumber={latestPageItemNumber}
        onNextLastPage={onNextLastPage}
        onPreviousFirstPage={onPreviousFirstPage}
        disabled={disabled}
        rowNumberSet={BIG_ROW_NUMBER_SET}
      />
    </div>
  );
};

export default TableDetail;
